// ProfessionalsPage.jsx — CONTAINER (dono da lógica). Monta { vm, actions } reais e renderiza a
// ProfessionalsView (View pura do Claude Design). Formato View/ViewModel à risca (docs/DESIGN-FLOW.md).
// O visual é da View; aqui há estado, chamadas à API e os dialogs (que continuam os de antes).
// A tradução para o vm mora em professionals/professionalsVmBuilder.js (função pura, coberta por
// professionals/__tests__/professionalsVmBuilder.test.js).
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useQuery, useQueries, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';

import placesService from '../services/placesService';
import photoService from '../services/photoService';
import accessService from '../services/accessService';
import schedulesService from '../services/schedulesService';
import appointmentsService from '../services/appointmentsService';
import usePlacesCRUD from '../hooks/usePlacesCRUD';
import { useProfessionals, useSchedules, useEntities } from '../hooks/queries';
import { useThemeCustom } from '../hooks/useThemeCustom';
import { PlacesDataProvider } from '../contexts/PlacesDataContext';
import useDialog from '../hooks/useDialog';
import useSnackbar from '../hooks/useSnackbar';

import AppSnackbar from '../components/ui/AppSnackbar';
import CascadeDeleteDialog from '../components/ui/CascadeDeleteDialog';
import ProfessionalForm from '../components/forms/ProfessionalForm';
import ProfilePhotoUpload from '../components/ProfilePhotoUpload';
import ShareProfessionalDialog from '../components/ShareProfessionalDialog';
import ProfessionalCustomPricesDialog from '../components/professionals/ProfessionalCustomPricesDialog';
import ProfessionalServicesDialog from '../components/places/ProfessionalServicesDialog';
import PlaceProfessionalsManager from '../components/places/PlaceProfessionalsManager';
import SmartWizard from '../components/wizard/SmartWizard';
import AppointmentFormDialog from '../components/AppointmentFormDialog';

import ProfessionalsView from './professionals/ProfessionalsView';
import { buildProfessionalsVm, chaveDosHorarios } from './professionals/professionalsVmBuilder';

// missingProfileFields[].key (contrato) → campo do ProfessionalForm que recebe o foco.
const CAMPO_DO_FORM = { specialty: 'specialty', title: 'title', registration: 'registrationNumber', phone: 'phone', email: 'email', bio: 'bio', description: 'description' };

export default function ProfessionalsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { getLabel } = useThemeCustom();

  // Leituras: a equipe (com preço do local, preço próprio, movimento e páginas), as agendas com as
  // métricas, o espelho com turnos e ausências, quem tem acesso a cada pessoa e — só para os
  // dialogs de conserto — os locais com o catálogo inteiro.
  const { items: resposta, loading: carregandoEquipe, error: erroEquipe, updateAsync, reload: recarregarEquipe } = useProfessionals();
  const { schedules, loading: carregandoAgendas, error: erroAgendas } = useSchedules();
  // refetchOnMount: quem volta de Agendas depois de mexer num turno precisa ver o veredito novo.
  const { entities, loading: carregandoEspelho, error: erroEspelho } = useEntities({ queryOptions: { refetchOnMount: 'always' } });
  const acessos = useQuery({ queryKey: ['access', 'overview'], queryFn: accessService.getOverview });
  const { data: dadosDosLocais, reload: recarregarLocais } = usePlacesCRUD(placesService);
  const data = useMemo(() => (Array.isArray(resposta) ? {} : resposta || {}), [resposta]);

  // Turnos das agendas de profissionais RECEBIDOS: o espelho só traz os da conta. Sem eles o
  // veredito diria "sem turno" de quem tem turno — acusar sem o dado é pior do que esperar.
  const agendasDeRecebidos = useMemo(() => {
    const recebidos = new Set((data.professionals || []).filter((p) => p.isShared).map((p) => p.id));
    return (schedules || []).filter((s) => recebidos.has(s.professionalId)).map((s) => s.id);
  }, [data.professionals, schedules]);
  const turnosDosRecebidos = useQueries({
    queries: agendasDeRecebidos.map((id) => ({ queryKey: ['schedules', id, 'shifts'], queryFn: () => schedulesService.getShifts(id) })),
  });
  const turnosExtras = useMemo(() => Object.fromEntries(
    agendasDeRecebidos.map((id, i) => [id, turnosDosRecebidos[i]?.data || []]),
  ), [agendasDeRecebidos, turnosDosRecebidos]);
  const carregandoTurnosRecebidos = turnosDosRecebidos.some((q) => q.isLoading);

  const formDialog = useDialog();
  const deleteDialog = useDialog();
  const photoDialog = useDialog();
  const shareDialog = useDialog();
  const customPricesDialog = useDialog();
  const servicesDialog = useDialog();
  const teamDialog = useDialog();
  const { snackbar, showSuccess, showError, hideSnackbar } = useSnackbar();

  const [showSmartWizard, setShowSmartWizard] = useState(false);
  const [agendamento, setAgendamento] = useState(null); // prefillConfig do AppointmentFormDialog
  const [searchText, setSearchText] = useState('');
  const [sortOrder, setSortOrder] = useState('name_asc');
  const [segment, setSegment] = useState('all');
  const [selectedId, setSelectedId] = useState(null);
  const [campoEmFoco, setCampoEmFoco] = useState(null);
  const [horarios, setHorarios] = useState({}); // "próximos horários" pedidos, por pessoa × local × serviço
  const [versaoDasFotos, setVersaoDasFotos] = useState(0); // foto nova → a URL ganha outro ?v=

  // ?create=1 / ?wizard=1 abrem o cadastro (links do onboarding e da Home).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('create') === '1' || params.get('wizard') === '1' || params.get('wizard') === 'true') {
      setShowSmartWizard(true);
      window.history.replaceState(null, '', location.pathname);
    }
  }, [location.pathname, location.search]);

  // Mexer em vínculo, serviço, preço ou acesso muda a equipe, as agendas e o espelho.
  const recarregarTudo = useCallback(() => {
    recarregarEquipe();
    recarregarLocais();
    queryClient.invalidateQueries({ queryKey: ['schedules'] });
    queryClient.invalidateQueries({ queryKey: ['entities'] });
    queryClient.invalidateQueries({ queryKey: ['access'] });
  }, [recarregarEquipe, recarregarLocais, queryClient]);

  // O tema devolve a própria chave quando não tem o rótulo ("professionalsExamples") — isso não é texto.
  const rotulo = useCallback((k) => { const v = getLabel(k); return v && v !== k ? v : null; }, [getLabel]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fotoDe = useCallback((donoId, id) => (donoId ? photoService.getProfilePhotoUrl(donoId, id) : ''), [versaoDasFotos]);

  const vm = useMemo(() => buildProfessionalsVm({
    data,
    schedules,
    entities: entities || null,
    turnosExtras,
    access: acessos.data || null,
    horarios,
    getLabel: rotulo,
    filters: { searchText, sortOrder, segment },
    selectedId,
    loading: carregandoEquipe || carregandoAgendas || carregandoEspelho || acessos.isLoading || carregandoTurnosRecebidos,
    error: erroEquipe || erroAgendas || erroEspelho || acessos.error,
    fotoDe,
  }), [data, schedules, entities, turnosExtras, acessos.data, acessos.isLoading, acessos.error, horarios, rotulo,
    searchText, sortOrder, segment, selectedId, carregandoEquipe, carregandoAgendas, carregandoEspelho,
    carregandoTurnosRecebidos, erroEquipe, erroAgendas, erroEspelho, fotoDe]);

  // A pessoa aberta foi excluída (ou sumiu na recarga): volta para a lista.
  useEffect(() => {
    if (selectedId && !carregandoEquipe && !(data.professionals || []).some((p) => p.id === selectedId)) setSelectedId(null);
  }, [selectedId, carregandoEquipe, data.professionals]);

  const acharPessoa = (id) => (data.professionals || []).find((p) => p.id === id);
  const acharLocal = (id) => (dadosDosLocais?.places || []).find((p) => p.id === id);
  const nomeDoServico = (id) => (data.services || []).find((s) => s.id === id)?.name;
  const agendaPorId = (id) => (schedules || []).find((s) => s.id === id);

  const handleSubmit = async (valores) => {
    try {
      await updateAsync(formDialog.data.id, valores);
      showSuccess(`Alterações em "${valores.name || formDialog.data.name}" salvas.`);
      formDialog.handleClose();
    } catch (err) {
      showError(`Não foi possível salvar: ${err.response?.data?.message || err.message}`);
    }
  };

  const handlePhotoUpload = async (file) => {
    const pessoa = photoDialog.data;
    if (!pessoa?.id) return;
    try {
      await photoService.uploadProfilePhoto(file, pessoa.id);
      photoService.savePhotoTimestamp(pessoa.id, Date.now());
      setVersaoDasFotos((v) => v + 1);
      photoDialog.handleClose();
      showSuccess('Foto atualizada.');
    } catch (err) {
      showError(`Não foi possível enviar a foto: ${err.response?.data?.message || err.message}`);
    }
  };

  const abrirAgendas = (scheduleId) => {
    const agenda = agendaPorId(scheduleId);
    navigate(agenda ? `/schedules?busca=${encodeURIComponent(agenda.name)}` : '/schedules');
  };

  /** Os serviços desta pessoa num local: o dialog de Locais, com o vínculo e o catálogo do local. */
  const abrirServicosNoLocal = (professionalId, placeId) => {
    const place = acharLocal(placeId);
    const link = (place?.professionals || []).find((l) => l.professionalId === professionalId);
    const pessoa = acharPessoa(professionalId);
    if (place && link) servicesDialog.handleOpen({ place, placeProfessional: { ...link, professional: pessoa, professionalName: pessoa?.name } });
  };

  /** Pôr a pessoa num local: com um local só (o caso mais comum), a equipe dele; senão, a tela de Locais. */
  const escolherOndeAtende = () => {
    const locais = dadosDosLocais?.places || [];
    if (locais.length === 1) teamDialog.handleOpen(locais[0]);
    else navigate(locais.length === 0 ? '/places?wizard=1' : '/places');
  };

  const actions = {
    onRetry: recarregarTudo,
    onSearchChange: setSearchText,
    onSortChange: setSortOrder,
    onSegmentChange: setSegment,
    onClearFilters: () => { setSearchText(''); setSegment('all'); },
    onNew: () => setShowSmartWizard(true),
    onOpen: (id) => setSelectedId(id),
    onCloseDetail: () => setSelectedId(null),
    onEdit: (id) => { const p = acharPessoa(id); if (p) { setCampoEmFoco(null); formDialog.handleOpen(p); } },
    // Abre a edição já no campo que falta (o formulário põe o foco nele ao terminar de abrir).
    onCompleteProfile: (id, fieldKey) => {
      const p = acharPessoa(id);
      if (p) { setCampoEmFoco(CAMPO_DO_FORM[fieldKey] || null); formDialog.handleOpen(p); }
    },
    onDelete: (id) => { const p = acharPessoa(id); if (p) deleteDialog.handleOpen(p); },
    onChangePhoto: (id) => { const p = acharPessoa(id); if (p) photoDialog.handleOpen(p); },
    onShare: (id) => { const p = acharPessoa(id); if (p) shareDialog.handleOpen(p); },
    onBook: (professionalId, placeId, serviceId, slotKey) => setAgendamento({
      professionalId, placeId: placeId || null, serviceId: serviceId || null, slotStartTime: slotKey || null,
    }),
    onFindNextSlots: async (professionalId, placeId, serviceId) => {
      const chave = chaveDosHorarios(professionalId, placeId, serviceId);
      setHorarios((h) => ({ ...h, [chave]: { state: 'loading' } }));
      try {
        const slots = await appointmentsService.getNextAvailableSlots(professionalId, serviceId, placeId, 3);
        setHorarios((h) => ({ ...h, [chave]: { state: 'loaded', slots: slots || [] } }));
      } catch {
        setHorarios((h) => ({ ...h, [chave]: { state: 'error' } }));
      }
    },
    onEditCustomPrices: (professionalId, placeId, serviceId) => {
      const pessoa = acharPessoa(professionalId);
      const link = (pessoa?.places || []).find((l) => l.placeId === placeId);
      const servico = (link?.services || []).find((s) => s.serviceId === serviceId);
      if (pessoa && servico) {
        const local = (data.places || []).find((p) => p.id === placeId);
        customPricesDialog.handleOpen({
          ...servico, placeId, placeName: local?.name, professionalName: pessoa.name, serviceName: nomeDoServico(serviceId),
        });
      }
    },
    onFix: (professionalId, fix) => {
      switch (fix?.type) {
        case 'linkPlace': return escolherOndeAtende();
        case 'configureServices': return abrirServicosNoLocal(professionalId, fix.placeId);
        case 'createSchedule': return navigate('/schedules?wizard=1');
        case 'openSchedule': return abrirAgendas(fix.scheduleId);
        default: return undefined;
      }
    },
    onOpenSchedule: abrirAgendas,
    onOpenPage: (url) => { if (url) window.open(url, '_blank', 'noopener,noreferrer'); },
    // Mesmo destino do "Aguardando confirmação" da Home: é em Agendamentos que o dono confirma.
    onOpenAwaitingConfirmation: () => navigate('/appointments'),
  };

  // Os dialogs de Locais (serviços da pessoa no local, equipe do local) leem o catálogo pelo contexto.
  return (
    <PlacesDataProvider data={dadosDosLocais}>
      <ProfessionalsView vm={vm} actions={actions} />

      {/* Edição do cadastro (o novo vai pelo assistente) */}
      <ProfessionalForm open={formDialog.open} onClose={formDialog.handleClose} onSubmit={handleSubmit} data={formDialog.data} focusField={campoEmFoco} />

      {/* Exclusão, com o impacto antes */}
      <CascadeDeleteDialog
        open={deleteDialog.open}
        onClose={deleteDialog.handleClose}
        onDeleted={() => {
          deleteDialog.handleClose();
          showSuccess(`Exclusão de "${deleteDialog.data?.name}" concluída.`);
          setSelectedId(null);
          recarregarTudo();
        }}
        deletePath={`/professionals/${deleteDialog.data?.id}`}
        entityName={deleteDialog.data?.name}
      />

      <ProfilePhotoUpload
        open={photoDialog.open}
        onClose={photoDialog.handleClose}
        onUpload={handlePhotoUpload}
        currentPhotoUrl={photoDialog.data ? fotoDe(photoDialog.data.ownerId, photoDialog.data.id) : null}
      />

      {/* Compartilhar com outra conta */}
      <ShareProfessionalDialog
        open={shareDialog.open}
        onClose={shareDialog.handleClose}
        professional={shareDialog.data}
        onSuccess={recarregarTudo}
      />

      {/* Preço próprio desta pessoa num serviço */}
      <ProfessionalCustomPricesDialog
        open={customPricesDialog.open}
        onClose={customPricesDialog.handleClose}
        placeServiceLink={customPricesDialog.data}
        placeId={customPricesDialog.data?.placeId}
        onUpdate={recarregarTudo}
      />

      {/* Serviços desta pessoa num local */}
      <ProfessionalServicesDialog
        open={servicesDialog.open}
        onClose={servicesDialog.handleClose}
        placeProfessional={servicesDialog.data?.placeProfessional}
        place={servicesDialog.data?.place}
        onUpdate={recarregarTudo}
      />

      {/* Equipe do local (pôr a pessoa no único local da conta) */}
      <PlaceProfessionalsManager open={teamDialog.open} onClose={teamDialog.handleClose} place={teamDialog.data} onUpdate={recarregarTudo} />

      {/* Cadastro guiado */}
      <SmartWizard
        open={showSmartWizard}
        wizardId="add-professional"
        onClose={() => setShowSmartWizard(false)}
        onSuccess={() => {
          setShowSmartWizard(false);
          recarregarTudo();
          showSuccess('Cadastro criado.');
        }}
      />

      {/* Novo agendamento já com a pessoa (e o local, o serviço e o horário, quando vierem) */}
      <AppointmentFormDialog
        open={!!agendamento}
        onClose={() => setAgendamento(null)}
        onSuccess={() => { setAgendamento(null); showSuccess('Agendamento criado.'); recarregarTudo(); }}
        prefillConfig={agendamento}
      />

      <AppSnackbar snackbar={snackbar} onClose={hideSnackbar} />
    </PlacesDataProvider>
  );
}
